import { cn, Input } from '@kalos/ui';
import { type ComponentProps, forwardRef } from 'react';

import { cleanMoney } from '../tools/cleanMoney';

type MoneyInputProps = Omit<ComponentProps<typeof Input>, 'type' | 'value' | 'onChange'> & {
  value: string;
  /** The cleaned text: digits and up to two decimal places, never more. */
  onValueChange: (value: string) => void;
};

/**
 * A dollar amount, the way money is usually entered on a phone: the keypad with a decimal
 * point, "$" fixed at the start, the figure right-aligned like the amounts it sits beside, no
 * spinner arrows, at most two decimal places, the whole value selected on focus so one tap
 * replaces it, and tidied to two places ("12" → "12.00") when the field is left.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ value, onValueChange, className, onFocus, onBlur, ...props }, ref) => (
    <div className="relative">
      <span
        aria-hidden
        className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm"
      >
        $
      </span>
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder="0.00"
        value={value}
        onChange={(event) => onValueChange(cleanMoney(event.target.value))}
        onFocus={(event) => {
          event.target.select();
          onFocus?.(event);
        }}
        onBlur={(event) => {
          const parsed = parseFloat(value);
          if (value.trim() !== '' && !isNaN(parsed)) onValueChange(parsed.toFixed(2));
          onBlur?.(event);
        }}
        className={cn('pl-6 text-right tabular-nums', className)}
        {...props}
      />
    </div>
  ),
);
MoneyInput.displayName = 'MoneyInput';
